import * as React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock, MapPin, Plus, Wrench } from 'lucide-react';
import type { Intervention } from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/components/ui/primitives';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from '~/components/shared/states';
import { ConfirmDialog } from '~/components/shared/confirm-dialog';
import { useCancelIntervention, useInterventions } from '~/lib/queries';
import { formatDuration, formatPrice, formatRelativeDay, formatSlotRange } from '~/lib/utils';

/** Interventions du client : a venir et historique. */
export function ClientInterventionsPage() {
  const upcoming = useInterventions({ scope: 'upcoming' });
  const past = useInterventions({ scope: 'past' });
  const cancel = useCancelIntervention();

  const [toCancel, setToCancel] = React.useState<Intervention | null>(null);

  return (
    <div className="app-page">
      <PageHeader
        title="Mes interventions"
        description="Suivez vos rendez-vous et consultez l'historique de vos entretiens."
        actions={
          <Button asChild>
            <Link to="/reserver">
              <Plus aria-hidden />
              Nouveau rendez-vous
            </Link>
          </Button>
        }
      />

      <Tabs defaultValue="upcoming">
        <TabsList>
          <TabsTrigger value="upcoming">A venir</TabsTrigger>
          <TabsTrigger value="past">Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="pt-4">
          {upcoming.isLoading ? <LoadingState /> : null}
          {upcoming.error ? (
            <ErrorState error={upcoming.error} onRetry={() => void upcoming.refetch()} />
          ) : null}

          {upcoming.data?.length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title="Aucun rendez-vous a venir"
              description="Prenez rendez-vous en moins de deux minutes."
              action={
                <Button asChild>
                  <Link to="/reserver">Prendre rendez-vous</Link>
                </Button>
              }
            />
          ) : null}

          <div className="flex flex-col gap-3">
            {upcoming.data?.map((intervention) => (
              <InterventionCard
                key={intervention.id}
                intervention={intervention}
                onCancel={() => setToCancel(intervention)}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="past" className="pt-4">
          {past.isLoading ? <LoadingState /> : null}
          {past.data?.length === 0 ? (
            <EmptyState icon={Wrench} title="Aucune intervention passee" />
          ) : null}

          <div className="flex flex-col gap-3">
            {past.data?.map((intervention) => (
              <InterventionCard key={intervention.id} intervention={intervention} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={!!toCancel}
        onOpenChange={(open) => !open && setToCancel(null)}
        title="Annuler ce rendez-vous ?"
        description={
          'Le creneau sera immediatement libere pour un autre client. ' +
          "L'annulation n'est possible que jusqu'a 24 heures avant l'intervention."
        }
        confirmLabel="Annuler le rendez-vous"
        cancelLabel="Conserver"
        variant="destructive"
        requireReason
        reasonLabel="Motif de l'annulation"
        loading={cancel.isPending}
        onConfirm={async (reason) => {
          if (!toCancel) return;
          await cancel.mutateAsync({ id: toCancel.id, input: { reason: reason ?? '' } });
          setToCancel(null);
        }}
      />
    </div>
  );
}

function InterventionCard({
  intervention,
  onCancel,
}: {
  intervention: Intervention;
  onCancel?: () => void;
}) {
  const cancellable = !!onCancel && ['BOOKED', 'CONFIRMED'].includes(intervention.status);

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 space-y-0.5">
            <p className="font-medium">{intervention.packageName}</p>
            <p className="text-xs text-muted-foreground">Reference {intervention.reference}</p>
          </div>
          <StatusBadge status={intervention.status} />
        </div>

        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="capitalize">{formatRelativeDay(intervention.startsAt)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="tabular-nums">
              {formatSlotRange(intervention.startsAt, intervention.endsAt)}
              <span className="text-muted-foreground">
                {' '}
                ({formatDuration(intervention.packageDurationMin)})
              </span>
            </span>
          </div>
          {intervention.address ? (
            <div className="flex items-center gap-2 sm:col-span-2">
              <MapPin className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">
                {intervention.address.street}, {intervention.address.postalCode}{' '}
                {intervention.address.city}
              </span>
            </div>
          ) : null}
        </dl>

        <Separator />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold tabular-nums">
            {formatPrice(intervention.totalAmount)}
          </span>
          {cancellable ? (
            <Button variant="outline" size="sm" onClick={onCancel}>
              Annuler
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
